import { useCallback, useEffect, useRef } from "react";
import {
    collectFrameGroups,
    mapGroupsWithoutFrameToFrames,
} from "@/appInterface/windows/ExportWindow/utils";
import type { EmojiCache } from "@/appInterface/windows/ExportWindow/VectorExport/emojiExportUtils";
import { canv } from "@/canv/global";
import { sortByIndexByParent } from "@/canvasController/helpers";
import type { frameWidget } from "@/elements/frame";
import { ImageWidget } from "@/elements/image";
import type { Group } from "@/group";
import { selectionManager } from "@/tools/cursorTool";
import PreviewWorker from "../preview.worker.ts?worker";

type FrameUID = string;
type PreviewResultType = "bitmap" | "blob";
type PreviewResult<T extends PreviewResultType> = T extends "bitmap"
    ? Record<FrameUID, ImageBitmap>
    : Record<FrameUID, string>;

const waitForIdle = () =>
    new Promise<void>((resolve) => {
        if ("requestIdleCallback" in window) {
            requestIdleCallback(() => resolve());
        } else {
            setTimeout(resolve, 16);
        }
    });

export const useFramePreviews = <T extends PreviewResultType = "blob">(
    frames: frameWidget[],
    resultType: T = "blob" as T,
) => {
    const resultRef = useRef<PreviewResult<T>>({} as PreviewResult<T>);
    const requestIdRef = useRef(0);
    const workerRef = useRef<Worker>();

    if (!workerRef.current) {
        workerRef.current = new PreviewWorker();
    }

    const cleanupResult = useCallback((data: PreviewResult<T>) => {
        Object.values(data).forEach((value) => {
            if (typeof value === "string") {
                URL.revokeObjectURL(value);
            } else if (value instanceof ImageBitmap) {
                value.close();
            }
        });
    }, []);

    const getBlobFromWorker = useCallback((bitmap: ImageBitmap, width: number, height: number) => {
        const worker = workerRef.current!;

        return new Promise<Blob>((resolve, reject) => {
            const cleanup = () => {
                worker.removeEventListener("message", handler);
                worker.removeEventListener("error", onError);
            };
            const handler = (e: MessageEvent) => {
                cleanup();
                resolve(e.data.blob);
            };
            const onError = (e: ErrorEvent) => {
                cleanup();
                reject(e);
            };

            worker.addEventListener("message", handler);
            worker.addEventListener("error", onError);

            worker.postMessage({ bitmap, width, height }, [bitmap]);
        });
    }, []);

    useEffect(() => {
        const worker = workerRef.current;

        return () => {
            worker?.terminate?.();
            if (resultRef.current) {
                const prev = resultRef.current;
                resultRef.current = {} as PreviewResult<T>;
                requestAnimationFrame(() => {
                    cleanupResult(prev);
                });
            }
        };
    }, [cleanupResult]);

    return useCallback(async (): Promise<PreviewResult<T>> => {
        const requestId = ++requestIdRef.current;
        // очищаем предыдущие URL сразу
        if (resultType === "blob" && resultRef.current) {
            const prev = resultRef.current;
            resultRef.current = {} as PreviewResult<T>;
            requestAnimationFrame(() => {
                cleanupResult(prev);
            });
        }

        const prev = canv.multiManager.disableMultiCursors;
        canv.multiManager.disableMultiCursors = true;
        canv.setIsHighPdfExporting(true);

        const groups = frames.map((frame) => frame.parent);
        const { groupsWithFrame, groupsWithoutFrame } = collectFrameGroups(groups);
        const prefetchPromises: Promise<void>[] = [];
        groupsWithoutFrame.forEach((group) => {
            if (group.isItemWrapper() && group.children[0] instanceof ImageWidget) {
                prefetchPromises.push((group.children[0] as ImageWidget).prefetchHighLod());
            }
        });

        // если устарело — выходим
        if (requestId !== requestIdRef.current) return {};

        const selectedGroups = canv.canvasController.getSelected();

        const groupsByFrames = mapGroupsWithoutFrameToFrames(groupsWithFrame, groupsWithoutFrame);
        const savedTransformation = {
            0: canv.m[0],
            3: canv.m[3],
            4: canv.m[4],
            5: canv.m[5],
        };

        const appCtx = canv.ctx;

        const emojiCache: EmojiCache = new Map();
        const result = {} as PreviewResult<T>;

        selectionManager.clearSelected();

        let offscreen: OffscreenCanvas | null = null;
        let offscreenCtx: OffscreenCanvasRenderingContext2D | null = null;

        try {
            await Promise.all(prefetchPromises);

            offscreen = new OffscreenCanvas(1, 1);
            offscreenCtx = offscreen.getContext("2d");

            if (!offscreenCtx) {
                throw new Error("Не удалось получить 2d контекст");
            }

            const sortedGroupsByFrames: Record<string, Group[]> = {};

            for (const key in groupsByFrames) {
                const arr = [...groupsByFrames[key]];
                sortByIndexByParent(arr);
                sortedGroupsByFrames[key] = arr;
            }

            for (let i = 0; i < groupsWithFrame.length; i++) {
                const frameGroup = groupsWithFrame[i];

                if (i % 2 === 0) {
                    await waitForIdle();
                }
                // проверка на устаревание внутри цикла
                if (requestId !== requestIdRef.current) {
                    if (resultType === "blob") {
                        cleanupResult(result);
                    }
                    return {};
                }

                canv.zoomToSelected([frameGroup], true);

                const visualWidth = (frameGroup.width / canv.dpr) * canv.m[0];
                const visualHeight = (frameGroup.height / canv.dpr) * canv.m[0];

                const targetWidth = visualWidth * canv.dpr;
                const targetHeight = visualHeight * canv.dpr;

                if (offscreen.width !== targetWidth) {
                    offscreen.width = targetWidth;
                }
                if (offscreen.height !== targetHeight) {
                    offscreen.height = targetHeight;
                }

                // !!! нельзя использовать getContextAttributes drawFocusIfNeeded,
                // так как offscreenCtx это OffscreenCanvasRenderingContext2D, а canv.ctx это CanvasRenderingContext2D
                canv.ctx = offscreenCtx as unknown as CanvasRenderingContext2D;

                canv.ctx.setTransform(
                    canv.m[0],
                    0,
                    0,
                    canv.m[3],
                    -frameGroup.startX * canv.m[0],
                    -frameGroup.startY * canv.m[0],
                );

                const groupsOnFrame = sortedGroupsByFrames[frameGroup.UID] || [];
                const groupsForPreview = [frameGroup, ...groupsOnFrame];

                canv.redraw();
                canv.redraw(true, true, groupsForPreview);

                const bitmap = offscreen.transferToImageBitmap();

                if (requestId !== requestIdRef.current) {
                    bitmap.close();
                    cleanupResult(result);
                    return {};
                }

                if (resultType === "bitmap") {
                    result[frameGroup.children[0].UID] = bitmap as PreviewResult<T>[FrameUID];
                } else {
                    const blob = await getBlobFromWorker(bitmap, offscreen.width, offscreen.height);

                    if (!blob) {
                        bitmap.close();
                        continue;
                    }

                    result[frameGroup.children[0].UID] = URL.createObjectURL(
                        blob,
                    ) as PreviewResult<T>[FrameUID];

                    bitmap.close();
                }

                canv.ctx.setTransform(1, 0, 0, 1, 0, 0);
                canv.ctx.clearRect(0, 0, offscreen.width, offscreen.height);
            }
        } catch (er) {
            console.log(er);
        } finally {
            emojiCache.clear();

            canv.ctx = appCtx;

            selectionManager.setSelected(selectedGroups);

            canv.m[0] = savedTransformation[0];
            canv.m[3] = savedTransformation[3];
            canv.m[4] = savedTransformation[4];
            canv.m[5] = savedTransformation[5];

            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
            canv.setIsHighPdfExporting(false);
            canv.multiManager.disableMultiCursors = prev;

            if (offscreen) {
                offscreen.width = 0;
                offscreen.height = 0;
            }
        }

        // финальная проверка перед сохранением
        if (requestId !== requestIdRef.current) {
            if (resultType === "blob") {
                cleanupResult(result);
            }
            return {};
        }
        resultRef.current = result;
        return result;
    }, [frames, getBlobFromWorker, cleanupResult, resultType]);
};
